/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Modal, SimpleHeader, OptionsButtons, Pin } from 'ui/components';

import * as ParentalControl from 'core/store/ducks/parentalControl';
import * as PinDuck from 'store/ducks/settings/managePin';
import { useDispatch, useSelector } from 'react-redux';
import { Ducks } from '@wit/vtv-x-foundation-lib';
import { useHistory } from 'react-router';
import { PrivateRoutePath } from '../../../routes/paths';
import translate from '../../../services/i18n/translate';
import { EChangePinState, EOptionType, IOption } from '../../../types/components/Settings';

export interface validatePin {
  newPin: string;
  confirmNewPin: string;
}
let validatePin = {
  newPin: '',
  confirmNewPin: '',
};

const ManagePinPage: React.FunctionComponent = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [pageAction, setPageAction] = useState<EOptionType>();
  // Get User preferences
  const defaultUser = useSelector(Ducks.Household.Selectors.data.getDefaultUser);

  // Activation States
  const isLoading = useSelector(Ducks.Household.Selectors.actions.updateUserPreference.isProcessing);
  const isComplete = useSelector(Ducks.Household.Selectors.actions.updateUserPreference.isProcessed);

  const [requestIsComplete, setRequestIsComplete] = useState<boolean>(false);
  useEffect(() => {
    setRequestIsComplete(isComplete);
  }, [isComplete]);

  // PIN VALIDATION
  const pinIsCorrect = useSelector(PinDuck.Selectors.getPinIsCorrect);
  const pinChangedSuccess = useSelector(PinDuck.Selectors.pinChangedSuccess);
  const pinIsMissmatchStore = useSelector(PinDuck.Selectors.getIsMissmatch);
  const pinIsLoading = useSelector(PinDuck.Selectors.isProcessing);
  const pinIsProcessed = useSelector(PinDuck.Selectors.isProcessed);

  // Retrieve ERRORS from managePin Duck
  const pinErrorPinDuck = useSelector(PinDuck.Selectors.getError);

  // Retrieve ERRORS from parentalControl Duck
  const pinErrorParentalDuck = useSelector(ParentalControl.Selectors.getError);

  const pinErrorMessage = pinErrorPinDuck || pinErrorParentalDuck;

  // Defines the title and backLink
  const prepareHeaderProps = {
    subtitle: translate.settingManagePin,
    backLabel: translate['menu.profile.settings'],
    backUrl: PrivateRoutePath.SETTINGS,
  };

  /* MODAL STUFF */
  /// ////// Change PIN /////////
  const [openChangePinDialog, setOpenChangePinDialog] = useState<boolean>(false);
  // Dialog - Open NEW PIN
  const [openInsertNewPinDialog, setOpenInsertNewPinDialog] = useState<boolean>(false);
  // Dialog - Open CONFIRM NEW PIN
  const [openConfirmNewPinDialog, setOpenConfirmNewPinDialog] = useState<boolean>(false);
  // Dialog - Changed PIN Success
  const [openChangePinSuccessDialog, setOpenChangePinSuccessDialog] = useState<boolean>(false);
  // Dialog for Missmatch PIN's - Try Again
  const [openPinMissmatchDialog, setOpenPinMissmatchDialog] = useState<boolean>(false);
  // Dialog - Pin Error
  const [openPinErrorDialog, setOpenPinErrorDialog] = useState<boolean>(false);
  // State - Pin Missmatch
  const [pinMissmatch, setPinMissmatch] = useState<boolean>(false);

  /// ////// Activate Parental /////////
  // Confirmation Message Dialog
  const [openConfirmActivateParentalDialog, setOpenConfirmActivateParentalDialog] = useState<boolean>(false);
  // Confirmation PIN Dialog
  const [openActivateParentalPinDialog, setOpenActivateParentalPinDialog] = useState<boolean>(false);
  // State of the Activation Prop
  const [activateParentalValidation, setActivateParentalValidation] = useState<boolean>(false);
  // State of the Button - Only set after the request is complete
  const [activatePinButtonState, setActivatePinButtonState] = useState<boolean>(false);
  // Defines the buttons present in the Options
  const [optionButtons, setOptionButtons] = useState<IOption[]>([]);

  useEffect(() => {
    setOptionButtons([
      {
        key: 'togglePin',
        text: activatePinButtonState ? translate['setting.pin.deactivate'] : translate['setting.pin.activate'],
        value: !activatePinButtonState,
        isSelected: false,
        type: EOptionType.ACTIVATE_PARENTAL,
        disabled: false,
      },
      {
        key: 'changePin',
        text: translate.settingChangePin,
        value: true,
        isSelected: false,
        type: EOptionType.CHANGE_PIN,
        disabled: false,
      },
    ]);
  }, [activatePinButtonState]);

  // ACTIVATE_PARENTAL - Init State
  useEffect(() => {
    if (defaultUser?.parentalValidation) {
      // Prop State
      setActivateParentalValidation(defaultUser?.parentalValidation);
      // Button State
      setActivatePinButtonState(defaultUser?.parentalValidation);
    }
  }, [defaultUser?.parentalValidation]);

  // ACTIVATE_PARENTAL - After complete update request, close PIN confirmation Dialog
  useEffect(() => {
    if (requestIsComplete && pageAction === EOptionType.ACTIVATE_PARENTAL) {
      // Close PIN
      setOpenActivateParentalPinDialog(false);
      // Close COnfirmation Activate Parental
      setOpenConfirmActivateParentalDialog(false);
      // Update Button Status
      setActivatePinButtonState(activateParentalValidation);
    }

    return () => {
      setRequestIsComplete(false);
    };
  }, [requestIsComplete, activateParentalValidation, pageAction]);

  // ACTIVATE_PARENTAL - Validate Current PIN
  useEffect(() => {
    if (pinIsCorrect === true && pageAction === EOptionType.ACTIVATE_PARENTAL) {
      dispatch(ParentalControl.ActionCreators.changeParentalValidation(activateParentalValidation));
    }
  }, [pinIsCorrect, dispatch, activateParentalValidation, pageAction]);

  // ACTIVATE_PARENTAL - Deactivate Parental
  const onConfirmDeactivation = () => {
    // Open PIN verification
    setOpenActivateParentalPinDialog(true);
    // Close Confirmation Dialog
    setOpenConfirmActivateParentalDialog(false);
  };

  // ACTIVATE_PARENTAL - Close Confirmation Dialog
  const onCloseConfirmationDialog = () => {
    setOpenConfirmActivateParentalDialog(false);
  };

  const controlChangePinFlow = useCallback(
    (state: EChangePinState) => {
      switch (state) {
        case EChangePinState.INIT:
          setPageAction(EOptionType.CHANGE_PIN);
          controlChangePinFlow(EChangePinState.RESET);
          controlChangePinFlow(EChangePinState.ASK_DEFAULT_PIN);
          break;
        case EChangePinState.ASK_DEFAULT_PIN:
          setOpenChangePinDialog(true);
          break;
        case EChangePinState.DEFAULT_PIN_VALIDATE:
          // Close Current PIN Dialog
          setOpenChangePinDialog(false);
          // Open Insert New PIN Dialog
          setOpenInsertNewPinDialog(true);
          break;
        case EChangePinState.ASK_NEW_PIN:
          break;
        case EChangePinState.CONFIRM_NEW_PIN:
          // Close Current PIN Dialog
          setOpenChangePinDialog(false);
          // Open CONFIRMATION NEW PIN
          setOpenConfirmNewPinDialog(true);
          // Close NEW PIN Dialog - First One
          setOpenInsertNewPinDialog(false);
          break;
        case EChangePinState.PINS_MATCH:
          break;
        case EChangePinState.PINS_MISSMATCH:
          // Close CONFIRMATION NEW PIN
          setOpenConfirmNewPinDialog(false);
          // Open Dialog - Try Again
          setOpenPinMissmatchDialog(true);
          break;
        case EChangePinState.PINS_MISSMATCH_TRY_AGAIN:
          dispatch(PinDuck.ActionCreators.pinReset());
          // Close Message Dialog
          setOpenPinMissmatchDialog(false);
          // Open NEW PIN Dialog - First One
          setOpenInsertNewPinDialog(true);
          validatePin = {
            newPin: '',
            confirmNewPin: '',
          };
          break;
        case EChangePinState.PIN_CHANGED_SUCCESS:
          setOpenConfirmNewPinDialog(false);
          setOpenChangePinSuccessDialog(true);
          break;
        case EChangePinState.PIN_CHANGED_ERROR:
          setOpenPinErrorDialog(true);
          break;
        case EChangePinState.PIN_CHANGED_SUCCESS_CLOSE_DIALOG:
          setOpenChangePinSuccessDialog(false);
          dispatch(ParentalControl.ActionCreators.setAgeRatingAskPin(true));
          dispatch(PinDuck.ActionCreators.pinReset());
          history.push(PrivateRoutePath.SETTINGS);
          break;
        case EChangePinState.RESET:
          setOpenChangePinDialog(false);
          setOpenInsertNewPinDialog(false);
          setOpenConfirmNewPinDialog(false);
          setOpenChangePinSuccessDialog(false);
          setOpenPinMissmatchDialog(false);
          setPinMissmatch(false);
          dispatch(PinDuck.ActionCreators.pinReset());
          break;
        default:
          break;
      }
    },
    [dispatch, history]
  );

  // Change PIn - Missmatch State
  useEffect(() => {
    if (pinIsMissmatchStore !== undefined) {
      setPinMissmatch(pinIsMissmatchStore);
    }
    return () => {
      setPinMissmatch(false);
    };
  }, [pinIsMissmatchStore, pinIsLoading, pinIsProcessed]);

  // CHANGE_PIN - Validate Current PIN
  useEffect(() => {
    if (pinIsCorrect === true && pageAction === EOptionType.CHANGE_PIN) {
      controlChangePinFlow(EChangePinState.DEFAULT_PIN_VALIDATE);
    }
  }, [pinIsCorrect, pageAction, controlChangePinFlow]);

  // CHANGE_PIN - PIN's Missmatch,
  useEffect(() => {
    if (pageAction === EOptionType.CHANGE_PIN) {
      if (pinMissmatch === true) {
        controlChangePinFlow(EChangePinState.PINS_MISSMATCH);
      } else if (requestIsComplete && pinChangedSuccess && !pinErrorPinDuck) {
        controlChangePinFlow(EChangePinState.PIN_CHANGED_SUCCESS);
      }
    }
  }, [pinMissmatch, controlChangePinFlow, pinChangedSuccess, requestIsComplete, pinErrorPinDuck, pageAction]);

  // CHANGE PIN - If has an error, popup a modal
  useEffect(() => {
    if (pinErrorMessage && pageAction === EOptionType.CHANGE_PIN) {
      controlChangePinFlow(EChangePinState.PIN_CHANGED_ERROR);
    }
    return () => {
      dispatch(ParentalControl.ActionCreators.resetErrors());
    };
  }, [controlChangePinFlow, dispatch, pageAction, pinErrorMessage]);

  // Close all Modals
  const onClosePinModal = () => {
    switch (pageAction) {
      case EOptionType.ACTIVATE_PARENTAL:
        // Activation Dialogs
        setOpenActivateParentalPinDialog(false);
        break;
      case EOptionType.CHANGE_PIN:
        // PIN Change Dialogs
        setOpenConfirmNewPinDialog(false);
        setOpenInsertNewPinDialog(false);
        setOpenChangePinDialog(false);
        break;
      default:
        break;
    }
  };

  const onOptionClick = (option: IOption) => {
    switch (option.type) {
      case EOptionType.ACTIVATE_PARENTAL:
        setPageAction(EOptionType.ACTIVATE_PARENTAL);
        dispatch(PinDuck.ActionCreators.pinReset());
        if (option.value === true) {
          // Open PIN Dialog to confirm
          setActivateParentalValidation(option.value);
          setOpenActivateParentalPinDialog(true);
        }
        // Open confirmation Dialog
        if (option.value === false) {
          setActivateParentalValidation(option.value);
          setOpenConfirmActivateParentalDialog(true);
        }
        break;
      case EOptionType.CHANGE_PIN:
        controlChangePinFlow(EChangePinState.INIT);
        break;
      default:
        break;
    }
  };

  // FOR ALL - PIN confirmation

  // CHANGE_PIN - Close Success Dialog
  const onCloseChangePinSuccessDialog = () => {
    controlChangePinFlow(EChangePinState.PIN_CHANGED_SUCCESS_CLOSE_DIALOG);
  };

  // CHANGE_PIN - INSERT NEW PIN
  const onNewPinHandler = (value?: string) => {
    if (value) {
      // Set the value to object
      validatePin.newPin = value;
      controlChangePinFlow(EChangePinState.CONFIRM_NEW_PIN);
    }
  };

  // CHANGE_PIN - Verify Both Pin's
  const onConfirmNewPinHandler = (value?: string) => {
    if (value) {
      // Set the value to object
      validatePin.confirmNewPin = value;
      // Send PIN to Validation
      dispatch(PinDuck.ActionCreators.pinValidateBoth(validatePin));
    }
  };

  // ERROR callback on Pin Missmatch
  const onTryAgainDialog = () => {
    controlChangePinFlow(EChangePinState.PINS_MISSMATCH_TRY_AGAIN);
  };

  const onErrorTryAgainDialog = () => {
    setOpenPinErrorDialog(false);
    controlChangePinFlow(EChangePinState.INIT);
  };

  const onCloseErrorDialog = () => {
    setOpenPinErrorDialog(false);
    controlChangePinFlow(EChangePinState.RESET);
  };

  // Component to Change Parental Pin
  const ChangeParentalPin = () => {
    return (
      <>
        <Modal setIsShowing={setOpenChangePinDialog} isShowing={openChangePinDialog}>
          <Pin closeModal={onClosePinModal} />
        </Modal>

        <Modal setIsShowing={setOpenInsertNewPinDialog} isShowing={openInsertNewPinDialog}>
          <Pin
            closeModal={onClosePinModal}
            onPinCallback={onNewPinHandler}
            title={translate.modalNewPinTitle}
            validate={false}
          />
        </Modal>

        <Modal setIsShowing={setOpenConfirmNewPinDialog} isShowing={openConfirmNewPinDialog}>
          <Pin
            closeModal={onClosePinModal}
            onPinCallback={onConfirmNewPinHandler}
            title={translate.modalConfirmNewPinTitle}
            validate={false}
          />
        </Modal>

        <Modal
          setIsShowing={setOpenChangePinSuccessDialog}
          isShowing={openChangePinSuccessDialog}
          title={translate['modal.title.success']}
          message={translate.modalNewPinSuccess}
          buttons={[{ id: 'ok', text: translate['modal.button.ok'], onClick: onCloseChangePinSuccessDialog }]}
        />

        <Modal
          setIsShowing={setOpenPinMissmatchDialog}
          isShowing={openPinMissmatchDialog}
          title={translate['modal.title.error']}
          message={translate.modalNewPinMismatch}
          buttons={[{ id: 'mismatchTryAgain', text: translate['modal.button.tryAgain'], onClick: onTryAgainDialog }]}
        />

        <Modal
          setIsShowing={setOpenPinErrorDialog}
          isShowing={openPinErrorDialog}
          title={translate['modal.title.error']}
          message={pinErrorMessage?.translation}
          buttons={[
            { id: 'errorTryAgain', text: translate['modal.button.tryAgain'], onClick: onErrorTryAgainDialog },
            { id: 'errorClose', text: translate['modal.button.close'], onClick: onCloseErrorDialog },
          ]}
        />
      </>
    );
  };

  return (
    <div className="template-holder back-nav component-inner component-inner-sub-settings manage-pin" id="Settings_pin">
      <ChangeParentalPin />
      <>
        <Modal setIsShowing={setOpenActivateParentalPinDialog} isShowing={openActivateParentalPinDialog}>
          <Pin closeModal={onClosePinModal} />
        </Modal>
        <Modal
          setIsShowing={setOpenConfirmActivateParentalDialog}
          isShowing={openConfirmActivateParentalDialog}
          title={!isLoading ? translate['modal.title.confirmation'] : ''}
          message={translate['setting.deactivate.parental.pin']}
          buttons={[
            { id: 'close', text: translate['modal.button.close'], onClick: onCloseConfirmationDialog },
            { id: 'confirm', text: translate['modal.button.confirm'], onClick: onConfirmDeactivation },
          ]}
        />
      </>
      <SimpleHeader
        subtitle={prepareHeaderProps.subtitle}
        backLabel={prepareHeaderProps.backLabel}
        backUrl={prepareHeaderProps.backUrl}
      />
      <div className="setting-container">
        <div className="container-options">
          <OptionsButtons options={optionButtons} onClickCallback={onOptionClick} bemBlock="PinSettings" />
        </div>
      </div>
    </div>
  );
};

export default ManagePinPage;
